import type { TinyAIPageContext } from '@repo/types';
import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useCurrentKitAndTinyAccount } from './useCurrentKitAndTinyAccount';
import { useTinyAIPageSource } from './useTinyAIPageSource';

/**
 * Hook to get current page context for TinyAI
 * Extracts context from URL including kitId, tinyAccountId, source, subMetricId, and actionPlanItemId
 */
export const useTinyAIPageContext = (): TinyAIPageContext => {
  const location = useLocation();
  const params = useParams<{
    subMetricId?: string;
    actionId?: string;
  }>();

  const { currentKitId, currentTinyAccountId } = useCurrentKitAndTinyAccount();
  const pageSource = useTinyAIPageSource();

  return useMemo(() => {
    const pathname = location.pathname;

    const baseContext: TinyAIPageContext = {
      url: pathname,
      source: pageSource,
    };

    if (currentKitId) {
      baseContext.kitId = currentKitId;
    }

    if (currentTinyAccountId) {
      baseContext.tinyAccountId = currentTinyAccountId;
    }

    const insightsMatch = pathname.match(/\/insights\/[^/]+\/(\d+)/);
    if (insightsMatch) {
      baseContext.subMetricId = insightsMatch[1];
    }

    const actionPlanMatch = pathname.match(/\/action\/(\d+)/);
    if (actionPlanMatch) {
      baseContext.actionPlanItemId = actionPlanMatch[1];
    }

    return baseContext;
  }, [
    location.pathname,
    params.subMetricId,
    params.actionId,
    currentKitId,
    currentTinyAccountId,
    pageSource,
  ]);
};
